import React, { createContext, useContext, useEffect, useState } from "react";
import { COMPANY_KEY, isTokenValidForCompany } from "../config/company";

const AuthContext = createContext();

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://api.cardapiobonfim.com";

// Renovação do guest em andamento (compartilhada por todas as requisições que receberem 401)
let pendingGuestLogin = null;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  // Erro do login guest ({ status, code, message }). code "TENANT004" = loja indisponível
  const [authError, setAuthError] = useState(null);

  // Função para fazer login guest
  // O guest é sempre de uma empresa: sem companyCode a API responde 400 (TENANT003)
  // silent: renovação no meio de uma requisição. Não altera `loading`, senão o App troca
  // as rotas pela tela de carregamento e desmonta o checkout com o pedido em andamento.
  const guestLogin = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setAuthError(null);

      const response = await fetch(`${BACKEND_URL}/api/auth/guest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Company-Code": COMPANY_KEY,
        },
        body: JSON.stringify({ companyCode: COMPANY_KEY }),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {}
        const code = errorData.code ?? errorData.error?.code;
        const message =
          code === "TENANT004"
            ? "Loja indisponível no momento."
            : (errorData.message ??
              errorData.error?.description ??
              `Falha no login guest: ${response.status}`);
        setAuthError({ status: response.status, code, message });
        const error = new Error(message);
        error.status = response.status;
        error.code = code;
        throw error;
      }

      const data = await response.json();
      const jwtToken = data.token || data.access_token || data.jwt;

      if (!jwtToken) {
        throw new Error("Token não encontrado na resposta da API");
      }

      // Salva o token no localStorage e no estado
      localStorage.setItem("guest_token", jwtToken);
      setToken(jwtToken);
      setIsAuthenticated(true);

      console.log("✅ Login guest realizado com sucesso");
      return jwtToken;
    } catch (error) {
      console.error("❌ Erro no login guest:", error);
      // Remove token inválido se existir
      localStorage.removeItem("guest_token");
      setToken(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Função para logout
  const logout = () => {
    localStorage.removeItem("guest_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  // Função para obter headers com autenticação
  const getAuthHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  // Renova o guest uma única vez, mesmo com várias requisições recebendo 401 ao mesmo tempo
  const renewGuestToken = () => {
    if (!pendingGuestLogin) {
      pendingGuestLogin = guestLogin({ silent: true }).finally(() => {
        pendingGuestLogin = null;
      });
    }
    return pendingGuestLogin;
  };

  // Função para fazer chamadas autenticadas para a API
  const apiCall = async (url, options = {}) => {
    // Se há uma renovação em andamento, espera ela terminar antes de chamar
    if (pendingGuestLogin) {
      await pendingGuestLogin.catch(() => {});
    }

    // Pega token do localStorage diretamente para garantir valor mais atual
    const currentToken = localStorage.getItem("guest_token");

    let authHeaders = {
      "Content-Type": "application/json",
    };

    if (currentToken) {
      authHeaders.Authorization = `Bearer ${currentToken}`;
    }

    let response = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    // Se não autenticado (401 — inclui TENANT005/TENANT006), renova o guest e refaz uma vez
    if (response.status === 401 && !url.includes("/api/auth/guest")) {
      console.log("🔄 Token inválido, renovando login guest...");

      try {
        // Só descarta o token se ninguém já o renovou nesse meio tempo
        if (localStorage.getItem("guest_token") === currentToken) {
          localStorage.removeItem("guest_token");
        }
        if (!localStorage.getItem("guest_token")) {
          await renewGuestToken();
        }

        // Pega o novo token do localStorage
        const newToken = localStorage.getItem("guest_token");

        authHeaders = {
          "Content-Type": "application/json",
        };

        if (newToken) {
          authHeaders.Authorization = `Bearer ${newToken}`;
        }

        // Refaz a chamada com o novo token
        response = await fetch(url, {
          ...options,
          headers: {
            ...authHeaders,
            ...options.headers,
          },
        });
      } catch (error) {
        console.error("❌ Erro ao renovar token guest:", error);
        throw error;
      }
    }

    return response;
  };

  // Verifica token existente no localStorage ao inicializar
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem("guest_token");

      if (savedToken) {
        // Só reaproveita token não expirado e com a claim company_code desta empresa
        // (tokens antigos sem a claim recebem 401 TENANT005 em toda requisição)
        if (isTokenValidForCompany(savedToken)) {
          setToken(savedToken);
          setIsAuthenticated(true);
          console.log("✅ Token guest válido encontrado no localStorage");
          setLoading(false);
          return;
        }
        console.log(
          "⚠️ Token expirado ou de outra empresa, removendo do localStorage",
        );
        localStorage.removeItem("guest_token");
      }

      // Se não tem token válido, faz login guest automaticamente
      try {
        console.log("🔑 Fazendo login guest inicial...");
        await guestLogin();
      } catch (error) {
        console.error("Falha no login guest inicial:", error);
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const value = {
    token,
    isAuthenticated,
    loading,
    authError,
    guestLogin,
    logout,
    getAuthHeaders,
    apiCall,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
